import React, { useState, useEffect } from 'react';
import { Card, Form, Button, Spinner, Alert } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { useHistory } from 'react-router-dom';
import PlacesAutocomplete, {
  geocodeByAddress,
  getLatLng,
} from 'react-places-autocomplete';
import { authService } from '../services/authService';
import api from '../config/ApiConfig';
import { GOOGLE_MAPS_API_KEY } from '../config/ApiConfig';
import Header from '../components/Layout/Header';
import Footer from '../components/Layout/Footer';

const AccountPage = () => {
  const history = useHistory();
  const [initialData, setInitialData] = useState(() => {
    const user = authService.getCurrentUser();
    return {
      nom: user?.nom || user?.user_profil?.nom || '',
      prenom: user?.prenom || user?.user_profil?.prenom || '',
      email: user?.email || '',
      telephone: user?.telephone || user?.user_profil?.telephone || '',
      raisonSocial: user?.user_profil?.RaisonSocial || '',
      adresseFacturation: user?.user_profil?.adresseFacturation || '',
      codePostal: user?.user_profil?.codePostal || '',
      ville: user?.user_profil?.ville || ''
    };
  });

  const [formData, setFormData] = useState(() => {
    const user = authService.getCurrentUser();
    return {
      nom: user?.nom || user?.user_profil?.nom || '',
      prenom: user?.prenom || user?.user_profil?.prenom || '',
      email: user?.email || '',
      telephone: user?.telephone || user?.user_profil?.telephone || '',
      raisonSocial: user?.user_profil?.RaisonSocial || '',
      adresseFacturation: user?.user_profil?.adresseFacturation || '',
      codePostal: user?.user_profil?.codePostal || '',
      ville: user?.user_profil?.ville || ''
    };
  });

  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showPasswordSection, setShowPasswordSection] = useState(false);
  const [isGMapsLoaded, setIsGMapsLoaded] = useState(false);

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [errors, setErrors] = useState({});

  // Charger Google Maps
  useEffect(() => {
    if (!window.google) {
      const gmapScriptEl = document.createElement('script');
      gmapScriptEl.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`;
      gmapScriptEl.async = true;
      gmapScriptEl.defer = true;
      gmapScriptEl.onload = () => setIsGMapsLoaded(true);
      document.body.appendChild(gmapScriptEl);
    } else {
      setIsGMapsLoaded(true);
    }
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSelectAddress = async (selectedAddress) => {
    try {
      const results = await geocodeByAddress(selectedAddress);
      const latLng = await getLatLng(results[0]);
      setFormData(prev => ({
        ...prev,
        adresseFacturation: selectedAddress
      }));
    } catch (error) {
      console.error('Erreur lors du géocodage :', error);
    }
  };

  const searchOptions = {
    componentRestrictions: { country: ['fr', 'mc'] },
    types: ['geocode', 'establishment'],
  };

  const validatePassword = (password) => {
    const regex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*\W).{8,}$/;
    return regex.test(password);
  };

  const handleUpdateProfile = async (e) => {
    console.log('🔴 handleUpdateProfile appelé !');
    e.preventDefault();
    setIsLoading(true);
    setErrors({});

    try {
      const currentUser = authService.getCurrentUser();
      const userId = currentUser.id;
      const payload = {
        nom: formData.nom,
        prenom: formData.prenom,
        telephone: formData.telephone,
        societe: formData.raisonSocial,
        adresseFacturation: formData.adresseFacturation,
        codePostal: formData.codePostal,
        ville: formData.ville
      };

      console.log('📤 Envoi de la requête API avec payload:', payload);
      const response = await api.post(`/users/update/${userId}`, payload);

      // Mettre à jour les données locales
      const updatedUser = {
        ...currentUser,
        nom: formData.nom,
        prenom: formData.prenom,
        telephone: formData.telephone,
        user_profil: {
          ...currentUser.user_profil,
          nom: formData.nom,
          prenom: formData.prenom,
          telephone: formData.telephone,
          RaisonSocial: formData.raisonSocial,
          adresseFacturation: formData.adresseFacturation,
          codePostal: formData.codePostal,
          ville: formData.ville
        }
      };
      localStorage.setItem('userData', JSON.stringify(updatedUser));

      // Mettre à jour initialData avec les nouvelles valeurs
      setInitialData({ ...formData });

      toast.success('Profil mis à jour avec succès');
      setIsEditing(false);
    } catch (err) {
      console.error(err);
      const errorMsg = err.response?.data?.message || 'Erreur lors de la mise à jour du profil';
      toast.error(errorMsg);
      setErrors({ general: errorMsg });
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdatePassword = async (e) => {
    e.preventDefault();
    setErrors({});

    if (!validatePassword(passwordData.newPassword)) {
      setErrors({ password: 'Le mot de passe doit contenir au moins 8 caractères, une majuscule, un chiffre et un caractère spécial.' });
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setErrors({ password: 'Les mots de passe ne correspondent pas' });
      return;
    }

    setIsLoading(true);

    try {
      const currentUser = authService.getCurrentUser();
      const userId = currentUser.id;
      await api.post(`/users/update/${userId}`, {
        current_password: passwordData.currentPassword,
        new_password: passwordData.newPassword
      });

      toast.success('Mot de passe modifié avec succès');
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      setShowPasswordSection(false);
    } catch (err) {
      console.error(err);
      const errorMsg = err.response?.data?.message || err.response?.data?.data || 'Erreur lors de la modification du mot de passe';
      toast.error(errorMsg);
      setErrors({ password: errorMsg });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Header />
      <div className="container mt-5 mb-5">
        <div className="row justify-content-center">
          <div className="col-lg-8">
            <Card className="shadow-sm">
              <Card.Header className="bg-primary text-white">
                <h4 className="mb-0">
                  <i className="fas fa-user-circle me-2"></i>
                  Mon compte
                </h4>
              </Card.Header>
              <Card.Body className="p-4">
                {errors.general && (
                  <Alert variant="danger">{errors.general}</Alert>
                )}

                <Form onSubmit={handleUpdateProfile}>
                  <h5 className="mb-3 text-primary">Informations personnelles</h5>

                <div className="row mb-3">
                  <div className="col-md-6">
                    <Form.Group>
                      <Form.Label>Prénom</Form.Label>
                      <Form.Control
                        type="text"
                        name="prenom"
                        value={isEditing ? formData.prenom : initialData.prenom}
                        onChange={handleInputChange}
                        disabled={!isEditing}
                      />
                    </Form.Group>
                  </div>
                  <div className="col-md-6">
                    <Form.Group>
                      <Form.Label>Nom</Form.Label>
                      <Form.Control
                        type="text"
                        name="nom"
                        value={isEditing ? formData.nom : initialData.nom}
                        onChange={handleInputChange}
                        disabled={!isEditing}
                      />
                    </Form.Group>
                  </div>
                </div>

                <Form.Group className="mb-3">
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={initialData.email}
                    disabled
                    className="bg-light"
                  />
                  <Form.Text className="text-muted">
                    L'email ne peut pas être modifié
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Téléphone</Form.Label>
                  <Form.Control
                    type="tel"
                    name="telephone"
                    value={isEditing ? formData.telephone : initialData.telephone}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Raison sociale (optionnel)</Form.Label>
                  <Form.Control
                    type="text"
                    name="raisonSocial"
                    value={isEditing ? formData.raisonSocial : initialData.raisonSocial}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                  />
                </Form.Group>

                <hr className="my-4" />
                <h5 className="mb-3 text-primary">Adresse de facturation</h5>

                <Form.Group className="mb-3">
                  <Form.Label>Adresse</Form.Label>
                  {isEditing && isGMapsLoaded ? (
                    <PlacesAutocomplete
                      value={formData.adresseFacturation}
                      onChange={(newValue) =>
                        setFormData(prev => ({ ...prev, adresseFacturation: newValue }))
                      }
                      onSelect={handleSelectAddress}
                      searchOptions={searchOptions}
                    >
                      {({
                        getInputProps,
                        suggestions,
                        getSuggestionItemProps,
                        loading,
                      }) => (
                        <div className="position-relative">
                          <Form.Control
                            {...getInputProps({
                              placeholder: 'Entrez votre adresse',
                            })}
                          />
                          {suggestions.length > 0 && (
                            <div
                              className="position-absolute w-100 bg-white border rounded shadow-sm"
                              style={{ zIndex: 1000, top: '100%', maxHeight: '200px', overflowY: 'auto' }}
                            >
                              {loading && <div className="p-2">Chargement...</div>}
                              {suggestions.map((suggestion) => (
                                <div
                                  {...getSuggestionItemProps(suggestion, {
                                    style: {
                                      backgroundColor: suggestion.active ? '#e9ecef' : '#fff',
                                      cursor: 'pointer',
                                      padding: '8px 12px',
                                    },
                                  })}
                                  key={suggestion.placeId}
                                >
                                  {suggestion.description}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </PlacesAutocomplete>
                  ) : (
                    <Form.Control
                      type="text"
                      name="adresseFacturation"
                      value={isEditing ? formData.adresseFacturation : initialData.adresseFacturation}
                      onChange={handleInputChange}
                      disabled={!isEditing}
                      placeholder="Numéro et nom de rue"
                    />
                  )}
                </Form.Group>

                <div className="row mb-4">
                  <div className="col-md-4">
                    <Form.Group>
                      <Form.Label>Code postal</Form.Label>
                      <Form.Control
                        type="text"
                        name="codePostal"
                        value={isEditing ? formData.codePostal : initialData.codePostal}
                        onChange={handleInputChange}
                        disabled={!isEditing}
                        placeholder="06000"
                      />
                    </Form.Group>
                  </div>
                  <div className="col-md-8">
                    <Form.Group>
                      <Form.Label>Ville</Form.Label>
                      <Form.Control
                        type="text"
                        name="ville"
                        value={isEditing ? formData.ville : initialData.ville}
                        onChange={handleInputChange}
                        disabled={!isEditing}
                        placeholder="Nice"
                      />
                    </Form.Group>
                  </div>
                </div>

                {isEditing && (
                  <div className="d-flex gap-2">
                    <Button
                      variant="success"
                      type="submit"
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <>
                          <Spinner as="span" animation="border" size="sm" className="me-2" />
                          Enregistrement...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-save me-2"></i>
                          Enregistrer
                        </>
                      )}
                    </Button>
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => {
                        setIsEditing(false);
                        // Restaurer les données initiales
                        setFormData({ ...initialData });
                      }}
                      disabled={isLoading}
                    >
                      Annuler
                    </Button>
                  </div>
                )}
              </Form>

              {!isEditing && (
                <Button
                  type="button"
                  variant="primary"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    console.log('✏️ Bouton Modifier cliqué');
                    setIsEditing(true);
                  }}
                >
                  <i className="fas fa-edit me-2"></i>
                  Modifier mes informations
                </Button>
              )}

              <hr className="my-4" />

              <h5 className="mb-3 text-primary">Liens rapides</h5>

              <div className="d-grid gap-2 mb-4">
                {/* HIDDEN - Loyalty program disabled
                <Button
                  variant="warning"
                  onClick={() => history.push('/loyalty')}
                >
                  <i className="fas fa-star me-2"></i>
                  Fidélité & Parrainage
                </Button>
                */}
                <Button
                  variant="info"
                  onClick={() => history.push('/historique')}
                >
                  <i className="fas fa-history me-2"></i>
                  Historique des livraisons
                </Button>
              </div>

              <hr className="my-4" />

              <h5 className="mb-3 text-primary">Sécurité</h5>

              {!showPasswordSection ? (
                <Button
                  variant="outline-primary"
                  onClick={() => setShowPasswordSection(true)}
                >
                  <i className="fas fa-key me-2"></i>
                  Changer mon mot de passe
                </Button>
              ) : (
                <Form onSubmit={handleUpdatePassword}>
                  {errors.password && (
                    <Alert variant="danger">{errors.password}</Alert>
                  )}

                  <Form.Group className="mb-3">
                    <Form.Label>Mot de passe actuel</Form.Label>
                    <Form.Control
                      type="password"
                      name="currentPassword"
                      value={passwordData.currentPassword}
                      onChange={handlePasswordChange}
                      required
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Nouveau mot de passe</Form.Label>
                    <Form.Control
                      type="password"
                      name="newPassword"
                      value={passwordData.newPassword}
                      onChange={handlePasswordChange}
                      required
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Confirmer le nouveau mot de passe</Form.Label>
                    <Form.Control
                      type="password"
                      name="confirmPassword"
                      value={passwordData.confirmPassword}
                      onChange={handlePasswordChange}
                      required
                    />
                  </Form.Group>

                  <div className="mb-3 p-3 bg-light rounded">
                    <small className="text-muted">
                      <i className="fas fa-info-circle me-2"></i>
                      Le mot de passe doit contenir au moins : 8 caractères, une majuscule, un chiffre et un caractère spécial
                    </small>
                  </div>

                  <div className="d-flex gap-2">
                    <Button
                      variant="success"
                      type="submit"
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <>
                          <Spinner as="span" animation="border" size="sm" className="me-2" />
                          Modification...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-check me-2"></i>
                          Modifier le mot de passe
                        </>
                      )}
                    </Button>
                    <Button
                      variant="secondary"
                      onClick={() => {
                        setShowPasswordSection(false);
                        setPasswordData({
                          currentPassword: '',
                          newPassword: '',
                          confirmPassword: ''
                        });
                        setErrors({});
                      }}
                      disabled={isLoading}
                    >
                      Annuler
                    </Button>
                  </div>
                </Form>
              )}
              </Card.Body>
            </Card>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default AccountPage;
